import { useState, useEffect } from "react";
import api from "../lib/api";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Scissors, Plus, Trash2, Zap, Recycle } from "lucide-react";

const COLORS = ["#38BDF8", "#0EA5E9", "#0284C7", "#6366F1", "#10B981", "#F59E0B", "#F43F5E", "#8B5CF6"];

export default function Nesting() {
  const location = useLocation();
  const [rollWidth, setRollWidth] = useState(140);
  const [margin, setMargin] = useState(2);
  const [pieces, setPieces] = useState(
    location.state?.pieces?.length
      ? location.state.pieces.map((p) => ({ name: p.name, width: p.width, height: p.height, qty: p.qty }))
      : [
          { name: "Panneau A", width: 60, height: 80, qty: 2 },
          { name: "Panneau B", width: 40, height: 50, qty: 3 },
          { name: "Renfort", width: 30, height: 30, qty: 4 },
        ]
  );
  const [result, setResult] = useState(null);
  const [invMats, setInvMats] = useState([]);
  const [selMat, setSelMat] = useState("");
  useEffect(() => { api.get("/inventory").then((r) => setInvMats(r.data || [])).catch(() => {}); }, []);
  const selMatObj = invMats.find((m) => m.id === selMat);

  const setPiece = (i, k, v) => {
    const p = [...pieces];
    p[i] = { ...p[i], [k]: k === "name" ? v : parseFloat(v) || 0 };
    setPieces(p);
  };

  const optimize = async () => {
    const offcuts = selMatObj ? (selMatObj.offcuts || []).map((o) => ({ id: o.id, width: o.width, height: o.height, quantity: o.quantity })) : [];
    const { data } = await api.post("/nesting/optimize", {
      roll_width_cm: rollWidth, margin_cm: margin, pieces, offcuts, material_id: selMat || null,
    });
    setResult(data);
    toast.success(data.offcut_pieces ? `Plan généré · ${data.offcut_pieces} pièce(s) recasée(s) sur chutes` : `Plan généré · ${data.efficiency}% d'efficacité`);
  };

  const scale = result ? 640 / result.roll_width_cm : 1;

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="text-3xl font-display font-bold text-navy">Plan d'Imposition</h1>
        <p className="text-slate-500 mt-1">Optimisez la découpe de vos pièces sur un rouleau pour minimiser les chutes.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 text-navy font-display font-semibold mb-4">
            <Scissors className="w-5 h-5 text-sky-500" /> Rouleau & pièces
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-xs font-medium text-slate-500">Laize du rouleau</label>
              <input type="number" value={rollWidth} onChange={(e) => setRollWidth(parseFloat(e.target.value) || 0)}
                data-testid="roll-width-input"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Marge (cm)</label>
              <input type="number" value={margin} onChange={(e) => setMargin(parseFloat(e.target.value) || 0)}
                data-testid="margin-input"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
          </div>

          <div className="mb-4 rounded-xl border border-cognac-200 bg-cognac-50/60 p-3">
            <label className="text-xs font-semibold text-cognac-700 inline-flex items-center gap-1.5"><Recycle className="w-4 h-4" /> Réutiliser les chutes en stock</label>
            <select value={selMat} onChange={(e) => setSelMat(e.target.value)} data-testid="nesting-material"
              className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cognac-400">
              <option value="">Aucune (rouleau neuf uniquement)</option>
              {invMats.filter((m) => (m.offcuts || []).length).map((m) => <option key={m.id} value={m.id}>{m.name} — {(m.offcuts || []).length} chute(s)</option>)}
            </select>
            {selMatObj && (selMatObj.offcuts || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="nesting-offcuts">
                {(selMatObj.offcuts || []).map((o) => <span key={o.id} className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-cognac-200 text-cognac-700 font-mono">{o.width}×{o.height}cm ×{o.quantity}</span>)}
              </div>
            )}
          </div>

          <div className="space-y-2">
            {pieces.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`piece-row-${i}`}>
                <input value={p.name} onChange={(e) => setPiece(i, "name", e.target.value)} placeholder="Nom"
                  className="col-span-4 px-2 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-sky-400" />
                <input type="number" value={p.width} onChange={(e) => setPiece(i, "width", e.target.value)} placeholder="L"
                  className="col-span-2 px-2 py-1.5 rounded-lg border border-slate-200 text-xs font-mono text-right focus:outline-none focus:ring-2 focus:ring-sky-400" />
                <input type="number" value={p.height} onChange={(e) => setPiece(i, "height", e.target.value)} placeholder="H"
                  className="col-span-2 px-2 py-1.5 rounded-lg border border-slate-200 text-xs font-mono text-right focus:outline-none focus:ring-2 focus:ring-sky-400" />
                <input type="number" value={p.qty} onChange={(e) => setPiece(i, "qty", e.target.value)} placeholder="Qté"
                  className="col-span-2 px-2 py-1.5 rounded-lg border border-slate-200 text-xs font-mono text-right focus:outline-none focus:ring-2 focus:ring-sky-400" />
                <button onClick={() => setPieces(pieces.filter((_, x) => x !== i))}
                  className="col-span-2 p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 justify-self-end">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-12 gap-1.5 text-[10px] text-slate-400 mt-1 px-1">
            <span className="col-span-4">Nom</span><span className="col-span-2 text-right">Larg</span>
            <span className="col-span-2 text-right">Haut</span><span className="col-span-2 text-right">Qté</span>
          </div>

          <button onClick={() => setPieces([...pieces, { name: `Pièce ${pieces.length + 1}`, width: 30, height: 30, qty: 1 }])}
            data-testid="add-piece-btn"
            className="mt-2 inline-flex items-center gap-2 text-sm text-sky-600 font-semibold hover:underline">
            <Plus className="w-4 h-4" /> Ajouter une pièce
          </button>

          <button onClick={optimize} data-testid="optimize-btn"
            className="mt-5 w-full py-3 rounded-xl bg-sky-400 text-navy font-semibold hover:bg-sky-500 transition-colors inline-flex items-center justify-center gap-2">
            <Zap className="w-4 h-4" /> Optimiser le plan
          </button>
        </div>

        <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 p-6">
          {result ? (
            <>
              {result.offcut_pieces > 0 && (
                <div className="mb-4 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-700 flex items-center gap-2" data-testid="offcut-summary">
                  <Recycle className="w-4 h-4 shrink-0" /> {result.offcut_pieces} pièce(s) recasée(s) sur vos chutes · {result.offcut_saved_m2} m² de matière économisée
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-emerald-50 rounded-xl p-3">
                  <div className="text-2xl font-display font-bold text-emerald-600 font-mono">{result.efficiency}%</div>
                  <div className="text-xs text-slate-500">Efficacité</div>
                </div>
                <div className="bg-rose-50 rounded-xl p-3">
                  <div className="text-2xl font-display font-bold text-rose-500 font-mono">{result.waste}%</div>
                  <div className="text-xs text-slate-500">Chutes</div>
                </div>
                <div className="bg-sky-50 rounded-xl p-3">
                  <div className="text-2xl font-display font-bold text-sky-600 font-mono">{result.total_length_m}m</div>
                  <div className="text-xs text-slate-500">Longueur</div>
                </div>
              </div>

              <div data-testid="nesting-plan-canvas">
                {result.bins && result.bins.length ? (
                  <div className="space-y-4">
                    {result.bins.map((b, bi) => <BinCanvas key={bi} bin={b} />)}
                  </div>
                ) : (
                  <>
                    <div className="text-xs text-slate-400 mb-2 font-mono">Rouleau {result.roll_width_cm}cm × {result.total_length_cm}cm</div>
                    <div className="border-2 border-navy rounded-lg overflow-auto scrollbar-thin bg-slate-50">
                      <svg width={result.roll_width_cm * scale} height={Math.max(result.total_length_cm * scale, 40)} style={{ display: "block" }}>
                        <defs>
                          <pattern id="grid" width={10 * scale} height={10 * scale} patternUnits="userSpaceOnUse">
                            <path d={`M ${10 * scale} 0 L 0 0 0 ${10 * scale}`} fill="none" stroke="#e2e8f0" strokeWidth="1" />
                          </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#grid)" />
                        {result.placements.map((p, i) => (
                          <g key={i}>
                            <rect x={p.x * scale} y={p.y * scale} width={p.w * scale} height={p.h * scale}
                              fill={p.overflow ? "#fecaca" : COLORS[i % COLORS.length]} fillOpacity="0.75"
                              stroke={p.overflow ? "#ef4444" : "#0F172A"} strokeWidth="1" rx="2" />
                            <text x={p.x * scale + 4} y={p.y * scale + 14} fontSize="10" fill="#0F172A" fontFamily="Poppins">{p.name}</text>
                          </g>
                        ))}
                      </svg>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 py-16">
              <Scissors className="w-8 h-8 mb-2 opacity-40" />
              Ajoutez vos pièces puis lancez l'optimisation.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function BinCanvas({ bin }) {
  const scale = bin.width_cm ? 640 / bin.width_cm : 1;
  const isOff = bin.type === "offcut";
  return (
    <div data-testid={`nesting-bin-${bin.type}`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-xs font-semibold inline-flex items-center gap-1.5 ${isOff ? "text-cognac-700" : "text-navy"}`}>
          {isOff ? <Recycle className="w-3.5 h-3.5" /> : <Scissors className="w-3.5 h-3.5" />}{bin.label}
        </span>
        <span className="text-[11px] font-mono text-slate-400">{bin.width_cm}cm × {bin.length_cm}cm</span>
      </div>
      <div className={`border-2 rounded-lg overflow-auto scrollbar-thin bg-slate-50 ${isOff ? "border-cognac-400" : "border-navy"}`}>
        <svg width={bin.width_cm * scale} height={Math.max(bin.length_cm * scale, 40)} style={{ display: "block" }}>
          {bin.placements.map((p, i) => (
            <g key={i}>
              <rect x={p.x * scale} y={p.y * scale} width={p.w * scale} height={p.h * scale}
                fill={p.overflow ? "#fecaca" : COLORS[i % COLORS.length]} fillOpacity="0.75"
                stroke={p.overflow ? "#ef4444" : "#0F172A"} strokeWidth="1" rx="2" />
              <text x={p.x * scale + 4} y={p.y * scale + 14} fontSize="10" fill="#0F172A" fontFamily="Poppins">{p.name}</text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}
